import { Link } from "react-router-dom";
import CountUp from "react-countup";
import VisibilitySensor from "react-visibility-sensor";
import { BsArrowRight } from "react-icons/bs";
import { useRef, useEffect, Fragment, useMemo } from "react";
import { IoMdArrowRoundForward } from "react-icons/io";
import { ReactComponent as Aicon } from "../../Asset/home-arrow-right.svg";
import { ReactComponent as Nice } from "../../Asset/niceLogo.svg";
import { AiOutlinePlus } from "react-icons/ai";
import { FiMinus } from "react-icons/fi";
import { useState } from "react";
import { ReactComponent as NiceLog } from "../../Asset/nice_1.svg";
import { ReactComponent as WhiteArror } from "../../Asset/greaterwhite.svg";
import { ReactComponent as RedArrrow } from "../../Asset/greaterred.svg";
import { ReactComponent as Greter } from "../../Asset/greaterT.svg";
import { ReactComponent as Less } from "../../Asset/lessT.svg";
import { ReactComponent as ShipLog } from "../../Asset/Group home.svg";
import { ReactComponent as PLay } from "../../Asset/play1.svg";
import Aos from "aos";
import "aos/dist/aos.css";
import "./Home.css";
import useFetchBlogs from "../../Hooks/useFetchBlogs";
import { BlogCardSkeleton, TrendingBannerSkeleton } from "../../Components/BlogSkeleton";

const items = [
  {
    id: 1,
    title: "Transport crew to and from offshore with ease.",
    image:
      "https://res.cloudinary.com/du44pwjoi/image/upload/v1742471102/psv_eny33s.png",
  },
  {
    id: 2,
    title: "Transport crew to and from offshore with ease.",
    image:
      "https://res.cloudinary.com/du44pwjoi/image/upload/v1742471102/ahts_pfl7kj.png",
  },
  {
    id: 3,
    title: "Transport crew to and from offshore with ease.",
    image:
      "https://res.cloudinary.com/du44pwjoi/image/upload/v1742471097/fsiv_kj79ls.png",
  },
  {
    id: 4,
    title: "Transport crew to and from offshore with ease.",
    image:
      "https://res.cloudinary.com/du44pwjoi/image/upload/v1742471097/csv_klkqpb.png",
  },
];

// Helper function to format date to "Nov 14" style
const formatDate = (dateString) => {
  if (!dateString) return "Jan 1";
  try {
    const date = new Date(dateString);
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const day = date.getDate();
    return `${month} ${day}`;
  } catch (e) {
    return "Jan 1";
  }
};

// Helper function to calculate read time
const calculateReadTime = (content) => {
  if (!content) return "3 min read";
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  const readTime = Math.ceil(wordCount / wordsPerMinute);
  return `${readTime} min read`;
};

// Helper function to truncate text
const truncateText = (text, maxLength) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + "...";
};

// Helper function to map blog data to component format
const mapBlogToContent = (blog) => {
  const fullTitle = blog.title || "Untitled";
  const fullContent = blog.excerpt || blog.description || blog.content || "";
  
  return {
    id: blog._id || blog.id,
    date: formatDate(blog.createdAt || blog.date || blog.publishedAt),
    min: blog.readTime || calculateReadTime(blog.content || blog.excerpt || blog.description),
    title: truncateText(fullTitle, 50), // Limit to ~50 chars like "U.S. Coast Guard Takes Over Cleanup of $3M Yacht"
    content: truncateText(fullContent, 200), // Limit to ~200 chars like the Lorem ipsum example
    img: blog.coverImage || blog.image || blog.featuredImage || blog.img || null,
    slug: blog.slug || null,
  };
};

const faqs = [
  {
    question:
      "What are the main challenges facing the Nigerian maritime industry?",
    answer:
      "Vatug Solution: Vatug simplifies logistics by consolidating the entire vessel chartering process—from inquiry to delivery—on one platform. This streamlined approach reduces operational bottlenecks, improves transparency, and lowers overall costs for stakeholders, directly addressing infrastructure and efficiency challenges.",
  },
  {
    question:
      "How does the government regulate maritime activities in Nigeria?",
    answer:
      "Vatug Solution: Vatug’s platform integrates regulatory compliance checks (such as KYC and vessel registration) into its workflow, ensuring that all charters and transactions adhere to Nigerian maritime laws. This built-in compliance reduces administrative burden and helps companies meet regulatory requirements.",
  },
  {
    question: "What is being done to improve port infrastructure?",
    answer:
      "Vatug Solution: Vatug supports port operators by providing a digital interface for managing charter bookings and logistics, reducing congestion and manual paperwork. The platform’s data-driven insights can also inform stakeholders about peak usage times, helping optimize port operations and infrastructure usage.",
  },
];

const Home = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(
    sessionStorage.getItem("bgImageLoaded") === "true"
  );
  const [currentTrendingIndex, setCurrentTrendingIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Fetch blogs from API
  const { blogs, isLoading: blogsLoading } = useFetchBlogs();
  
  // Map blogs to content format - show first 6
  const contents = useMemo(() => {
    return blogs.length > 0 
      ? blogs.slice(0, 6).map(mapBlogToContent)
      : [];
  }, [blogs]);

  // Get trending blogs (first 3-5 blogs for rotation) - memoized to prevent unnecessary recalculations
  const trendingBlogs = useMemo(() => {
    return blogs.length > 0 
      ? blogs.slice(0, Math.min(5, blogs.length)).map(mapBlogToContent)
      : [];
  }, [blogs]);

  // Auto-rotate trending stories with smooth transitions
  useEffect(() => {
    if (trendingBlogs.length <= 1) return;
    
    const interval = setInterval(() => {
      // Start fade out
      setIsTransitioning(true);
      
      // After fade out completes, change content and fade in
      setTimeout(() => {
        setCurrentTrendingIndex((prev) => (prev + 1) % trendingBlogs.length);
        // Small delay before fade in
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 500); // Half of transition duration
    }, 5000); // Change every 5 seconds (allowing time for transition)

    return () => clearInterval(interval);
  }, [trendingBlogs.length]);

  const currentTrendingBlog = trendingBlogs[currentTrendingIndex] || null;

  const handleToggleAnswer = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // useEffect(() => {
  //   if (!imageLoaded) {
  //     const img = new Image();
  //     img.src =
  //       "https://res.cloudinary.com/du44pwjoi/image/upload/v1737629070/aerial-view-oil-and-gas-tanker-ship-oil-depot-term-2025-01-10-04-44-06-utc_1_j6wouk.png";
  //     img.onload = () => {
  //       setImageLoaded(true);
  //       sessionStorage.setItem("bgImageLoaded", "true");
  //     };
  //   }
  // }, [imageLoaded]);

  useEffect(() => {
    if (!imageLoaded) {
      // Only preload the critical hero image
      const criticalImage = "https://res.cloudinary.com/du44pwjoi/image/upload/v1737629070/aerial-view-oil-and-gas-tanker-ship-oil-depot-term-2025-01-10-04-44-06-utc_1_j6wouk.png";
      
      const img = new Image();
      img.src = criticalImage;
      
      // Show page after critical image loads OR after 2 seconds timeout (whichever comes first)
      const timeout = setTimeout(() => {
        setImageLoaded(true);
        sessionStorage.setItem("bgImageLoaded", "true");
      }, 2000);
      
      img.onload = () => {
        clearTimeout(timeout);
        setImageLoaded(true);
        sessionStorage.setItem("bgImageLoaded", "true");
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        setImageLoaded(true);
        sessionStorage.setItem("bgImageLoaded", "true");
      };
    }
  }, [imageLoaded]);


  const [scrollProgress, setScrollProgress] = useState(0);
  const [clonedItems, setClonedItems] = useState([]);
  const [hasCounted, setHasCounted] = useState(false);

  useEffect(() => {
    setClonedItems([...items, ...items]);
  }, []);

  useEffect(() => {
    Aos.init({ duration: 1000 });
  }, []);

  const itemListRef = useRef(null);

  const scrollToNextItem = () => {
    if (!itemListRef.current) return;

    const maxScrollAmount = 300; // Scroll by 300px
    const { scrollLeft, scrollWidth, clientWidth } = itemListRef.current;

    // Scroll normally
    itemListRef.current.scrollBy({
      left: maxScrollAmount,
      behavior: "smooth",
    });

    // If near the end of the cloned list, reset without visible jump
    if (scrollLeft + clientWidth >= scrollWidth - maxScrollAmount) {
      setTimeout(() => {
        itemListRef.current.scrollTo({ left: 0, behavior: "auto" });
      }, 500); // Small delay to allow smooth transition
    }
  };

  const scrollToPrevItem = () => {
    if (itemListRef.current) {
      const maxScrollAmount = 300; // Limit to 300px
      const { scrollLeft } = itemListRef.current;

      // Scroll the smaller of maxScrollAmount or the remaining distance to the start
      const scrollAmount = Math.min(maxScrollAmount, scrollLeft);

      if (scrollAmount > 0) {
        itemListRef.current.scrollBy({
          left: -scrollAmount,
          behavior: "smooth",
        });
      }
    }
  };

  // Auto-scroll every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      scrollToNextItem();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (itemListRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = itemListRef.current;
        const totalScrollableWidth = scrollWidth - clientWidth;
        const progress = (scrollLeft / totalScrollableWidth) * 100;
        setScrollProgress(progress);
      }
    };

    const scrollableElement = itemListRef.current;
    if (scrollableElement) {
      scrollableElement.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial calculation

      return () =>
        scrollableElement.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const currentDate = new Date();
  const dayWithZero = String(currentDate.getDate()).padStart(2, "0");

  const formattedDate = `${dayWithZero} ${currentDate.toLocaleDateString(
    "en-NG",
    { month: "long" }
  )} ${currentDate.getFullYear()}`;

  return (
    <Fragment>
      {!imageLoaded && (
        <div className="full-loader">
          <div className="custom-loader"></div>
        </div>
      )}
      {imageLoaded && (
        <div className="vessel-mother">
          <div className={`vessel-home ${imageLoaded ? "loaded" : "loading"}`}>
            <div className="vessel-overlay">
              <Nice />
              <div className="con-1">
                <h3 data-aos="zoom-in">
                  Seamlessly Connecting <span>Maritime</span> Trade
                </h3>
                <p>
                  Your on-demand solution to connect charterers and shipowners,
                  efficiently and instantly
                </p>
                <div className="links">
                  <Link to="/our_vessels">
                    Find a Vessel <WhiteArror />
                  </Link>
                  <Link to="/jobs">
                    Jobs <RedArrrow />
                  </Link>
                </div>
              </div>
              <div className="con-2">
                {blogsLoading ? (
                  <TrendingBannerSkeleton />
                ) : currentTrendingBlog ? (
                  <Link
                    to={currentTrendingBlog.slug ? `/news/${currentTrendingBlog.slug}` : "/news"}
                    className={`story-1 trending-story-banner ${isTransitioning ? 'fade-out' : 'fade-in'}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <div 
                      className="image" 
                      style={{
                        backgroundImage: currentTrendingBlog.img 
                          ? `url(${currentTrendingBlog.img})` 
                          : 'url("https://res.cloudinary.com/du44pwjoi/image/upload/v1737630324/a-massive-cargo-ship-wood-chips-carrier-in-the-sea-2023-12-15-02-23-17-utc_fimshu.png")'
                      }}
                    ></div>
                    <span>
                      <div>
                        <h3>TRENDING STORIES</h3>
                        <BsArrowRight className="icon" />
                      </div>
                      <p>{currentTrendingBlog.title}</p>
                    </span>
                  </Link>
                ) : (
                  <div className="story-1">
                    <div className="image"></div>
                    <span>
                      <div>
                        <h3>TRENDING STORIES</h3>
                        <BsArrowRight className="icon" />
                      </div>
                      <p>U.S. Coast Guard Takes Over Cleanup of $3M Yacht</p>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="banner">
            <div className="box">
              <NiceLog />
              <div className="inner">
                <div className="wrap">
                  <div className="box">
                    <p>RECENTLY LISTED</p>
                    <VisibilitySensor
                      partialVisibility
                      offset={{ bottom: 200 }}
                      delayedCall
                      onChange={(isVisible) => {
                        if (isVisible && !hasCounted) {
                          setHasCounted(true);
                        }
                      }}
                    >
                      {({ isVisible }) => (
                        <h3>
                          {hasCounted ? (
                            <CountUp end={30} duration={10} />
                          ) : (
                            "30"
                          )}
                          +
                        </h3>
                      )}
                    </VisibilitySensor>
                  </div>
                  <div className="line"></div>
                  <div className="box">
                    <p>AVERAGE PRICE</p>
                    <h3>$5k</h3>
                  </div>
                </div>
                {/* <p>Last updated: 04 January 2025</p> */}
                <p>Last updated: {formattedDate}</p>
                <Link to="/our_vessels">
                  Find a Vessel <BsArrowRight />
                </Link>
              </div>
            </div>
            <div className="recently-added">
              <div
                className="item-list"
                ref={itemListRef}
                style={{
                  display: "flex",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {clonedItems.map((item, index) => (
                  <div
                    key={index}
                    className="item-card"
                    style={{ flexShrink: 0, width: "300px" }}
                  >
                    <img src={item.image} alt={item.title} />
                    <p>{item.title}</p>
                  </div>
                ))}
              </div>
              <div className="nav">
                <div className="scroll">
                  <div className="scroll-progress">
                    <div
                      className="progress-bar"
                      style={{ width: `${scrollProgress}%` }}
                    ></div>
                  </div>
                </div>
                <div className="in">
                  <Less onClick={scrollToPrevItem} className="icon-1" />
                  <Greter onClick={scrollToNextItem} className="icon-2" />
                </div>
              </div>
            </div>
          </div>

          <div className="refactor">
            <div className="wrapper">
              <div className="prob">
                <div className="box-1">
                  <p>Problem statement</p>
                  <p>
                    Problem <br /> statement
                  </p>
                  <div className="card">
                    <h3>Simplifying Maritime Operations</h3>
                    <Link to="/our_vessels">
                      Find a Vessel <BsArrowRight />
                    </Link>
                  </div>
                </div>
                <div className="box">
                  <p>
                    Our platform connects charterers and shipowners,
                    streamlining vessel chartering, crewing, logistics, and
                    acquisition. We facilitate faster, easier, and more
                    transparent transactions by automating documentation,
                    brokerage, staffing, and sales.
                  </p>
                  <p>
                    Addressing tight schedules, evolving regulations, and the
                    need for reliable collaborations, we ensure efficiency at
                    every step.
                  </p>
                  <Link>
                    Learn More <BsArrowRight />
                  </Link>
                </div>
              </div>
              <div className="box-2">
                <div className="img"></div>
                <ShipLog />
              </div>
              <div className="inspect">
                <div className="card">
                  <div className="in">
                    <p>vessel inspection</p>
                    <IoMdArrowRoundForward />
                  </div>
                  <div className="over">
                    <Aicon />
                  </div>
                </div>
                <div className="card">
                  <div className="in">
                    <p>Hire a vessel</p>
                    <IoMdArrowRoundForward />
                  </div>
                  <div className="over">
                    <Aicon />
                  </div>
                </div>
                <div className="card">
                  <div className="in">
                    <p>Buy a vessel</p>
                    <IoMdArrowRoundForward />
                  </div>
                  <div className="over">
                    <Aicon />
                  </div>
                </div>
                <div className="card">
                  <div className="in">
                    <p>Security Service</p>
                    <IoMdArrowRoundForward />
                  </div>
                  <div className="over">
                    <Aicon />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="service">
            <div className="wrapper">
              <div className="box-1">
                <p>OUR UNIQUE SOLUTION</p>
                <div className="card">
                  <h3>The All-in-One Digital Maritime Platform</h3>
                </div>
              </div>
              <div className="box-2">
                <p>
                  Solve maritime challenges. Our all-in-one platform simplifies
                  chartering, crewing, compliance, and payments for both vessel
                  owners and charterers.
                </p>
                <Link to="/stake_holder">
                  Learn More <RedArrrow />
                </Link>
              </div>
            </div>
          </div>

          <div className="who-we-are">
            <div className="wrapper">
              <div className="pic">
                <div className="over">
                  <div className="content">
                    <PLay />
                    <p>
                      Since our inception in 2020, we have been dedicated to
                      pioneering groundbreaking technology solutions for the
                      future of transport
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="service">
            <div className="wrapper">
              <div className="box-1">
                <p>STAKEHOLDERS ENGAGEMENT</p>
                <div className="card">
                  <h3>Lead the Way in Maritime Innovation.</h3>
                </div>
              </div>
              <div className="box-2">
                <p>
                  The maritime industry is at a crossroads. Technological
                  advancements, evolving regulations, and the pressing need for
                  sustainability demand a new approach. Join us in shaping the
                  future of maritime. Together, we can unlock new efficiencies,
                  drive innovation, and build a more sustainable and connected
                  maritime industry.
                </p>
                <Link to="/stake_holder">
                  Learn More <RedArrrow />
                </Link>
              </div>
            </div>
          </div>

          <div className="tech">
            <div className="wrapper">
              <div className="pic"></div>
              <Nice />
            </div>
          </div>

          <div className="Faq-container">
            <div className="wrapper">
              <h3>Frequently Asked Questions</h3>
              <div className="questions">
                <ul>
                  {faqs.map((faq, index) => (
                    <li
                      key={index}
                      className={activeIndex === index ? "open" : ""}
                    >
                      <span onClick={() => handleToggleAnswer(index)}>
                        <p>{faq.question}</p>
                        <span className="icons">
                          {activeIndex === index ? (
                            <FiMinus />
                          ) : (
                            <AiOutlinePlus />
                          )}
                        </span>
                      </span>
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <Link to="/faqs">
                Read More <WhiteArror />
              </Link>
            </div>
          </div>

          <div className="what">
            <div className="wrapper">
              <h3>Latest news and features</h3>
              {blogsLoading ? (
                <div className="card-container">
                  {[...Array(6)].map((_, index) => (
                    <BlogCardSkeleton key={index} />
                  ))}
                </div>
              ) : contents.length > 0 ? (
                <>
                  <div className="card-container">
                    {contents.map((item, index) => (
                      <Link
                        key={item.id || index}
                        to={item.slug ? `/news/${item.slug}` : "/news"}
                        className={`item-card ${
                          index === 1 ? "highlight-card" : ""
                        }`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <div className="meta">
                          <p>{item.date}</p>
                          <p>{item.min}</p>
                        </div>
                        <h3>{item.title}</h3>
                        <p>{item.content}</p>
                        {item.img && <img src={item.img} alt="" loading="lazy" />}
                        <div className="card-arrow">
                          <BsArrowRight />
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link to="/news">
                    Read More <WhiteArror />
                  </Link>
                </>
              ) : (
                <div className="card-container">
                  <div className="item-card">
                    <div className="meta">
                      <p>No news</p>
                      <p>-</p>
                    </div>
                    <h3>No news available</h3>
                    <p>Check back later for the latest updates.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default Home;
